import { useRef, useState } from 'react';
import cmitLogo from '../assets/cmit-logo.png';
import {
  Avatar,
  DeleteButton,
  Empty,
  ErrorBanner,
  Field,
  InlineEdit,
  Loading,
  Modal,
  PageHead,
} from '../components/ui';
import { api } from '../lib/api';
import { verdictBadge } from '../lib/format';
import { useResource } from '../lib/hooks';
import type { AccountabilityChart, Employee, Role, Seat } from '../lib/types';

/**
 * The accountability chart.
 *
 * A chart of SEATS, not people. A seat exists whether or not anyone sits in
 * it, which is what makes empty seats and one-person-three-seats visible —
 * the findings this page exists to surface.
 */
export function AccountabilityChartPage() {
  const chart = useResource<AccountabilityChart>('/chart/current');
  const employees = useResource<Employee[]>('/employees?status=active');
  const roles = useResource<Role[]>('/roles');
  const [editing, setEditing] = useState<Seat | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sheet = useRef<HTMLDivElement>(null);
  /* Drag and drop: the seat being dragged, where it would land, and a save in flight. */
  const [dragId, setDragId] = useState<number | null>(null);
  const [drop, setDrop] = useState<DropTarget | null>(null);
  const [moving, setMoving] = useState(false);
  /* Dragging a person: who, from which seat, and where they would land (before which holder, or at the end). */
  const [person, setPerson] = useState<{ id: number; seatId: number } | null>(null);
  const [personAt, setPersonAt] = useState<{ seatId: number; beforeId: number | null } | null>(null);

  if (chart.loading) return <Loading label="Loading the chart" />;
  if (chart.error) return <ErrorBanner message={chart.error} />;
  if (!chart.data) return <Empty title="No chart found" />;

  const c = chart.data;
  const stats = countSeats(c.seats);

  const handle = (err: unknown) => setError(err instanceof Error ? err.message : 'Could not save');

  /* Export: one landscape US Letter page (11 × 8.5 in) — the CMIT Solutions
     logo and the title across the top, the whole chart scaled down to fit
     under it. The print sheet is laid out off-screen at full size so it can
     be measured; zoom then shrinks it to the printable area (0.4in margins:
     10.2 × 7.7 in at 96px an inch, less the header). Save as PDF from the
     print dialog. */
  const exportChart = () => {
    const el = sheet.current;
    const tree = el?.querySelector<HTMLElement>('.chart-tree');
    if (!el || !tree) return;
    tree.style.zoom = '1';
    const width = 10.2 * 96;
    const height = 7.7 * 96 - 80;
    const scale = Math.min(1, width / tree.scrollWidth, height / tree.scrollHeight);
    tree.style.zoom = String(Math.floor(scale * 1000) / 1000);
    document.body.classList.add('printing-chart');
    const done = () => {
      document.body.classList.remove('printing-chart');
      window.removeEventListener('afterprint', done);
    };
    window.addEventListener('afterprint', done);
    window.print();
  };

  /* Moving a seat by dragging it. Onto the middle of another seat: it now
     reports to that seat, last among its reports. Onto a seat's left or right
     edge: beside it, under the same manager. Onto the top strip: a top-level
     seat. Siblings are renumbered 10, 20, 30… so the order sticks; the API
     refuses a move under one of the seat's own reports. */
  const all = flatten(c.seats);
  const byId = new Map(all.map((s) => [s.SeatId, s]));
  const dragged = dragId === null ? undefined : byId.get(dragId);
  const blocked = new Set(dragged ? collectIds(dragged) : []);

  const moveSeat = async (target: DropTarget) => {
    if (!dragged) return;
    let parent: number | null;
    let siblings: Seat[];
    let at: number;
    if (target.where === 'root') {
      parent = null;
      siblings = c.seats.filter((s) => s.SeatId !== dragged.SeatId);
      at = siblings.length;
    } else {
      const t = byId.get(target.seatId);
      if (!t || blocked.has(t.SeatId)) return;
      if (target.where === 'into') {
        parent = t.SeatId;
        siblings = t.children.filter((s) => s.SeatId !== dragged.SeatId);
        at = siblings.length;
      } else {
        parent = t.ParentSeatId;
        const pool = parent === null ? c.seats : (byId.get(parent)?.children ?? []);
        siblings = pool.filter((s) => s.SeatId !== dragged.SeatId);
        at = siblings.findIndex((s) => s.SeatId === t.SeatId) + (target.where === 'after' ? 1 : 0);
      }
    }
    const ordered = [...siblings];
    ordered.splice(at, 0, dragged);
    setMoving(true);
    setError(null);
    try {
      for (const [i, s] of ordered.entries()) {
        const sortOrder = (i + 1) * 10;
        if (s.SeatId === dragged.SeatId) {
          await api.put(`/chart/seats/${s.SeatId}`, { parentSeatId: parent, sortOrder });
        } else if (s.SortOrder !== sortOrder) {
          await api.put(`/chart/seats/${s.SeatId}`, { sortOrder });
        }
      }
    } catch (err) {
      handle(err);
    }
    setMoving(false);
    chart.reload();
  };

  /* Moving a person: within their seat it reorders the holders; onto another
     seat it moves them there, at the place dropped. */
  const movePerson = async (target: { seatId: number; beforeId: number | null }) => {
    if (!person) return;
    const from = byId.get(person.seatId);
    const to = byId.get(target.seatId);
    const who = from?.assignments.find((a) => a.AssignmentId === person.id);
    if (!from || !to || !who) return;
    if (to.SeatId !== from.SeatId && to.assignments.some((a) => a.EmployeeId === who.EmployeeId)) {
      setError(`${who.EmployeeName} already holds ${to.Name}.`);
      return;
    }
    const ids = to.assignments.map((a) => a.AssignmentId).filter((id) => id !== person.id);
    const at = target.beforeId === null ? ids.length : Math.max(0, ids.indexOf(target.beforeId));
    ids.splice(at, 0, person.id);
    if (to.SeatId === from.SeatId && ids.every((id, i) => id === from.assignments[i]?.AssignmentId)) return;
    setMoving(true);
    setError(null);
    try {
      if (to.SeatId !== from.SeatId) await api.put(`/chart/assignments/${person.id}`, { seatId: to.SeatId });
      await api.put(`/chart/seats/${to.SeatId}/assignments/order`, { ids });
    } catch (err) {
      handle(err);
    }
    setMoving(false);
    chart.reload();
  };

  const dnd: Dnd = {
    person,
    personAt,
    personStart: (id, seatId) => {
      setPerson({ id, seatId });
      setPersonAt(null);
    },
    personOver: (seatId, beforeId) => {
      if (personAt?.seatId !== seatId || personAt.beforeId !== beforeId) setPersonAt({ seatId, beforeId });
    },
    personLand: () => {
      const t = personAt;
      if (t) void movePerson(t);
      setPerson(null);
      setPersonAt(null);
    },
    personEnd: () => {
      setPerson(null);
      setPersonAt(null);
    },
    dragId,
    drop,
    start: (id) => {
      setDragId(id);
      setDrop(null);
    },
    over: (t) => {
      if (t.where !== 'root' && blocked.has(t.seatId)) return false;
      if (drop?.where !== t.where || (t.where !== 'root' && drop.where !== 'root' && drop.seatId !== t.seatId)) setDrop(t);
      return true;
    },
    land: () => {
      const t = drop;
      setDragId(null);
      setDrop(null);
      if (t) void moveSeat(t);
    },
    end: () => {
      setDragId(null);
      setDrop(null);
    },
  };

  const addRoot = () =>
    void api
      .post(`/chart/${c.ChartId}/seats`, { name: 'New area', sortOrder: c.seats.length * 10 })
      .then(chart.reload)
      .catch(handle);

  return (
    <>
      <PageHead
        title="Accountability Chart"
        lede="Every area of the business, and who is accountable for it. If an area has nobody in it, or one person holds three, that is the finding — not a formatting problem."
        actions={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={exportChart}
              disabled={c.seats.length === 0}
              title="One landscape 8.5 × 11 page with the CMIT Solutions logo — choose Save as PDF to export"
            >
              Export PDF
            </button>
            <button type="button" className="btn btn-primary" onClick={addRoot}>
              Add top-level area
            </button>
          </>
        }
      />

      {error && <ErrorBanner message={error} />}

      <div className="grid grid-4 mb-4">
        <div className="stat">
          <div className="label">Areas</div>
          <div className="figure">{stats.total}</div>
          <div className="foot">on the chart</div>
        </div>
        <div className="stat">
          <div className="label">Vacant</div>
          <div className={`figure${stats.vacant > 0 ? ' accent' : ''}`}>{stats.vacant}</div>
          <div className="foot">nobody accountable</div>
        </div>
        <div className="stat">
          <div className="label">Employees</div>
          <div className="figure">{stats.people}</div>
          <div className="foot">named on the chart</div>
        </div>
        <div className="stat" title={stats.manyHats.join(', ') || undefined}>
          <div className="label">Wearing many hats</div>
          <div className={`figure${stats.manyHats.length > 0 ? ' accent' : ''}`}>{stats.manyHats.length}</div>
          <div className="foot">
            {stats.manyHats.length > 0 && stats.manyHats.length <= 2
              ? stats.manyHats.join(', ')
              : 'people in 2+ areas'}
          </div>
        </div>
      </div>

      {c.seats.length === 0 ? (
        <Empty title="The chart is empty">
          <p>Start with the top area — usually Visionary or Integrator — and build downwards.</p>
        </Empty>
      ) : (
        <div className="card">
          <div className="card-body">
            <p className="xs muted mb-2 no-print">
              {moving
                ? 'Moving…'
                : 'Drag an area onto another area to move it under that area, or onto its left or right edge to place it beside it. Drag a person to reorder them in their area, or into another area.'}
            </p>
            {dragId !== null && (
              <div
                className={`chart-root-drop${drop?.where === 'root' ? ' on' : ''}`}
                onDragOver={(e) => {
                  if (dnd.over({ where: 'root' })) e.preventDefault();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  dnd.land();
                }}
              >
                Drop here to make it a top-level area
              </div>
            )}
            <div className="chart-scroll">
              <div className="chart-tree">
                <div className="chart-children">
                  {c.seats.map((seat) => (
                    <SeatNode key={seat.SeatId} seat={seat} onEdit={setEditing} dnd={moving ? undefined : dnd} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* The export: off-screen until printed. */}
      {c.seats.length > 0 && (
        <div className="chart-print-sheet" ref={sheet} aria-hidden="true">
          <div className="chart-print-head">
            <img src={cmitLogo} alt="CMIT Solutions" />
            <div className="grow">
              <div className="chart-print-title">Accountability Chart</div>
              <div className="chart-print-sub">
                {stats.total} areas · {stats.people} employees · {stats.vacant} vacant ·{' '}
                {new Date().toLocaleDateString([], { day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
            </div>
          </div>
          <div className="chart-tree">
            <div className="chart-children">
              {c.seats.map((seat) => (
                <SeatNode key={seat.SeatId} seat={seat} />
              ))}
            </div>
          </div>
        </div>
      )}

      {editing && (
        <SeatEditor
          seat={editing}
          chartId={c.ChartId}
          employees={employees.data ?? []}
          roles={roles.data ?? []}
          allSeats={flatten(c.seats)}
          onClose={() => setEditing(null)}
          onChanged={() => {
            chart.reload();
            /* Keep the modal open on the refreshed copy so adding three
               responsibilities in a row does not mean reopening three times. */
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

/** Where a dragged seat would land. */
type DropTarget = { where: 'root' } | { where: 'into' | 'before' | 'after'; seatId: number };
interface Dnd {
  /* A person being dragged, and where they would land. */
  person: { id: number; seatId: number } | null;
  personAt: { seatId: number; beforeId: number | null } | null;
  personStart: (assignmentId: number, seatId: number) => void;
  personOver: (seatId: number, beforeId: number | null) => void;
  personLand: () => void;
  personEnd: () => void;
  dragId: number | null;
  drop: DropTarget | null;
  start: (seatId: number) => void;
  /** Returns whether the target accepts the seat being dragged. */
  over: (target: DropTarget) => boolean;
  land: () => void;
  end: () => void;
}

/** One seat and everything under it. Without onEdit (the export) it has no
 *  edit control; without dnd it cannot be dragged. */
function SeatNode({ seat, onEdit, dnd }: { seat: Seat; onEdit?: (seat: Seat) => void; dnd?: Dnd }) {
  const vacant = seat.assignments.length === 0;
  const here = dnd?.drop && dnd.drop.where !== 'root' && dnd.drop.seatId === seat.SeatId ? dnd.drop.where : null;
  const personHere = dnd?.person && dnd.personAt?.seatId === seat.SeatId ? dnd.personAt : null;

  return (
    <div className="chart-node">
      <div
        className={`seat${dnd ? ' draggable' : ''}${dnd?.dragId === seat.SeatId ? ' dragging' : ''}${here ? ` drop-${here}` : ''}${personHere ? ' person-over' : ''}`}
        draggable={Boolean(dnd)}
        onDragStart={(e) => {
          if (!dnd) return;
          e.stopPropagation();
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', String(seat.SeatId));
          dnd.start(seat.SeatId);
        }}
        onDragOver={(e) => {
          /* A person over the box (not over a holder): they would go to the end of this seat. */
          if (dnd?.person) {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'move';
            dnd.personOver(seat.SeatId, null);
            return;
          }
          if (!dnd || dnd.dragId === null || dnd.dragId === seat.SeatId) return;
          e.stopPropagation();
          const r = e.currentTarget.getBoundingClientRect();
          const x = (e.clientX - r.left) / r.width;
          const where = x < 0.25 ? 'before' : x > 0.75 ? 'after' : 'into';
          if (dnd.over({ where, seatId: seat.SeatId })) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
          }
        }}
        onDrop={(e) => {
          if (!dnd) return;
          e.preventDefault();
          e.stopPropagation();
          if (dnd.person) dnd.personLand();
          else dnd.land();
        }}
        onDragEnd={() => dnd?.end()}
      >
        <div className="seat-head">
          <span>{seat.Name}</span>
          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit(seat)}
              aria-label={`Edit ${seat.Name}`}
              style={{
                background: 'none',
                border: 'none',
                color: 'inherit',
                cursor: 'pointer',
                opacity: 0.7,
                fontSize: 'var(--text-xs)',
              }}
            >
              edit
            </button>
          )}
        </div>
        <div className="seat-body">
          {vacant ? (
            <div className="seat-vacant">Vacant area</div>
          ) : (
            seat.assignments.map((a, i) => (
              <div
                key={a.AssignmentId}
                className={`seat-holder${dnd ? ' person-draggable' : ''}${dnd?.person?.id === a.AssignmentId ? ' dragging' : ''}${
                  personHere && personHere.beforeId === a.AssignmentId ? ' drop-above' : ''
                }${personHere && personHere.beforeId === null && i === seat.assignments.length - 1 ? ' drop-below' : ''}`}
                draggable={Boolean(dnd)}
                title={dnd ? 'Drag to reorder, or into another area' : undefined}
                onDragStart={(e) => {
                  if (!dnd) return;
                  /* Not the seat's drag: this person only. */
                  e.stopPropagation();
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', `person:${a.AssignmentId}`);
                  dnd.personStart(a.AssignmentId, seat.SeatId);
                }}
                onDragOver={(e) => {
                  if (!dnd?.person) return;
                  e.preventDefault();
                  e.stopPropagation();
                  e.dataTransfer.dropEffect = 'move';
                  const r = e.currentTarget.getBoundingClientRect();
                  const upper = e.clientY - r.top < r.height / 2;
                  const next = seat.assignments[i + 1]?.AssignmentId ?? null;
                  dnd.personOver(seat.SeatId, upper ? a.AssignmentId : next);
                }}
                onDrop={(e) => {
                  if (!dnd?.person) return;
                  e.preventDefault();
                  e.stopPropagation();
                  dnd.personLand();
                }}
                onDragEnd={(e) => {
                  e.stopPropagation();
                  dnd?.personEnd();
                }}
              >
                <Avatar name={a.EmployeeName} />
                <div className="grow">
                  <div className="strong small">{a.EmployeeName}</div>
                  {a.RoleTitle && <div className="xs muted">{a.RoleTitle}</div>}
                </div>
                {a.GwcVerdict && a.GwcVerdict !== 'yes' && (
                  <span className={`badge ${verdictBadge(a.GwcVerdict)}`} title="Latest GWC verdict">
                    {a.GwcVerdict === 'no' ? 'GWC' : '?'}
                  </span>
                )}
              </div>
            ))
          )}

          {seat.responsibilities.length > 0 && (
            <ul className="seat-roles">
              {seat.responsibilities.map((r) => (
                <li key={r.ResponsibilityId}>{r.Text}</li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {seat.children.length > 0 && (
        <div className="chart-children-wrap">
          <div className="chart-children">
            {seat.children.map((child) => (
              <div key={child.SeatId} className="chart-branch">
                <SeatNode seat={child} onEdit={onEdit} dnd={dnd} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SeatEditor({
  seat,
  chartId,
  employees,
  roles,
  allSeats,
  onClose,
  onChanged,
}: {
  seat: Seat;
  chartId: number;
  employees: Employee[];
  roles: Role[];
  allSeats: Seat[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const [name, setName] = useState(seat.Name);
  const [roleId, setRoleId] = useState<number | ''>(seat.RoleId ?? '');
  const [parentSeatId, setParentSeatId] = useState<number | ''>(seat.ParentSeatId ?? '');
  const [newResponsibility, setNewResponsibility] = useState('');
  const [assignEmployeeId, setAssignEmployeeId] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handle = (err: unknown) => setError(err instanceof Error ? err.message : 'Could not save');

  /* Cannot re-parent under itself or any of its own descendants — the API
     rejects it too, but not offering it avoids a pointless round trip. */
  const descendants = new Set(collectIds(seat));
  const parentOptions = allSeats.filter((s) => !descendants.has(s.SeatId));

  const saveDetails = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.put(`/chart/seats/${seat.SeatId}`, {
        name: name.trim(),
        roleId: roleId === '' ? null : Number(roleId),
        parentSeatId: parentSeatId === '' ? null : Number(parentSeatId),
      });
      onChanged();
    } catch (err) {
      handle(err);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Area — ${seat.Name}`}
      onClose={onClose}
      footer={
        <>
          <DeleteButton
            what={`the "${seat.Name}" area`}
            onConfirm={() => void api.del(`/chart/seats/${seat.SeatId}`).then(onChanged).catch(handle)}
          />
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
          <button type="button" className="btn btn-primary" disabled={busy || !name.trim()} onClick={() => void saveDetails()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <Field label="Area name" hint="The function, not the person — “Operations”, not “Dana”.">
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>

      <div className="form-grid">
        <Field label="Reports to">
          <select
            className="select"
            value={parentSeatId}
            onChange={(e) => setParentSeatId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">Top level</option>
            {parentOptions.map((s) => (
              <option key={s.SeatId} value={s.SeatId}>
                {s.Name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Linked role">
          <select
            className="select"
            value={roleId}
            onChange={(e) => setRoleId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">None</option>
            {roles.map((r) => (
              <option key={r.RoleId} value={r.RoleId}>
                {r.Title}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {/* --- Who sits here ------------------------------------------------ */}
      <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-4)' }}>
        <label className="field" style={{ marginBottom: 'var(--space-3)' }}>
          <span style={{ display: 'block' }} />
        </label>
        <div className="eyebrow mb-4">Who sits here</div>

        {seat.assignments.length === 0 && <p className="muted small">Vacant.</p>}
        {seat.assignments.map((a) => (
          <div key={a.AssignmentId} className="row-between" style={{ marginBottom: 8 }}>
            <div className="row">
              <Avatar name={a.EmployeeName} />
              <span className="small strong">{a.EmployeeName}</span>
            </div>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() => void api.del(`/chart/assignments/${a.AssignmentId}`).then(onChanged).catch(handle)}
            >
              Remove
            </button>
          </div>
        ))}

        <div className="row mt-4">
          <select
            className="select"
            value={assignEmployeeId}
            onChange={(e) => setAssignEmployeeId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">Choose a person…</option>
            {employees
              .filter((e) => !seat.assignments.some((a) => a.EmployeeId === e.EmployeeId))
              .map((e) => (
                <option key={e.EmployeeId} value={e.EmployeeId}>
                  {e.FullName}
                </option>
              ))}
          </select>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={assignEmployeeId === ''}
            onClick={() =>
              void api
                .post(`/chart/seats/${seat.SeatId}/assignments`, { employeeId: Number(assignEmployeeId) })
                .then(onChanged)
                .catch(handle)
            }
          >
            Assign
          </button>
        </div>
      </div>

      {/* --- The 5 roles --------------------------------------------------- */}
      <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-4)' }}>
        <div className="eyebrow mb-4">Roles &amp; responsibilities</div>
        <p className="xs muted">Aim for about five. More than that and the area is probably two areas.</p>

        <div className="mt-4">
          {seat.responsibilities.map((r) => (
            <div key={r.ResponsibilityId} className="row" style={{ alignItems: 'flex-start' }}>
              <div className="grow">
                <InlineEdit
                  value={r.Text}
                  rows={1}
                  onSave={(text) =>
                    void api.put(`/chart/responsibilities/${r.ResponsibilityId}`, { text }).then(onChanged).catch(handle)
                  }
                />
              </div>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() =>
                  void api.del(`/chart/responsibilities/${r.ResponsibilityId}`).then(onChanged).catch(handle)
                }
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div className="row mt-4">
          <input
            className="input"
            placeholder="Add a responsibility"
            value={newResponsibility}
            onChange={(e) => setNewResponsibility(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' || !newResponsibility.trim()) return;
              void api
                .post(`/chart/seats/${seat.SeatId}/responsibilities`, {
                  text: newResponsibility.trim(),
                  sortOrder: seat.responsibilities.length * 10,
                })
                .then(onChanged)
                .catch(handle);
            }}
          />
        </div>
      </div>

      {/* --- Add a report -------------------------------------------------- */}
      <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-4)' }}>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() =>
            void api
              .post(`/chart/${chartId}/seats`, {
                name: 'New area',
                parentSeatId: seat.SeatId,
                sortOrder: seat.children.length * 10,
              })
              .then(onChanged)
              .catch(handle)
          }
        >
          + Add an area reporting to this one
        </button>
      </div>
    </Modal>
  );
}

/* --- helpers -------------------------------------------------------------- */

function flatten(seats: Seat[]): Seat[] {
  return seats.flatMap((s) => [s, ...flatten(s.children ?? [])]);
}

function collectIds(seat: Seat): number[] {
  return [seat.SeatId, ...(seat.children ?? []).flatMap(collectIds)];
}

/* Employees are counted once each, however many areas they hold. */
function countSeats(seats: Seat[]) {
  const all = flatten(seats);
  const occupancy = new Map<number, { name: string; seats: number }>();
  for (const s of all) {
    for (const a of s.assignments) {
      const o = occupancy.get(a.EmployeeId) ?? { name: a.EmployeeName, seats: 0 };
      o.seats += 1;
      occupancy.set(a.EmployeeId, o);
    }
  }
  return {
    total: all.length,
    people: occupancy.size,
    vacant: all.filter((s) => s.assignments.length === 0).length,
    manyHats: [...occupancy.values()].filter((o) => o.seats > 1).map((o) => o.name),
  };
}
